import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Flex, VStack, HStack, Text, Divider, Button } from '@chakra-ui/react'
import { MenuItem } from '../MenuItem/MenuItem'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { ReactComponent as Logo } from './../../assets/logo.svg'
import { useLocation } from 'react-router-dom'
import { createDashboardRolesApi } from './../../utils/api-interface'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../store/store'
import { clearToken } from '../../store/slices/authSlice'
import { useTranslation } from 'react-i18next'
import { DashboardProfile } from './../../services/network/model/dashboard-profile'
import { setDashboardProfile } from '../../store/slices/DashboardUserSlice'
import { log } from '../../utils/logger'
import axios from 'axios'

function Sidebar(): JSX.Element {
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation()
  const [errorData, setErrorData] = useState<unknown>(null)
  const [loading, isLoading] = useState(false)

  const dashboardUser: DashboardProfile | null = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const canSeeRoles = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR
  }, [dashboardUser])

  const canSeeActivities = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title !== process.env.REACT_APP_ROLE_OFFICE
  }, [dashboardUser])

  const canSeeReviews = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR || dashboardUser.role.title === process.env.REACT_APP_ROLE_BACKOFFICE
  }, [dashboardUser])

  const handleLogOut = (): void => {
    dispatch(clearToken())
  }

  const fetchDashboardProfile = useCallback(async (): Promise<void> => {
    isLoading(true)
    try {
      const dashboardRolesApi = createDashboardRolesApi()
      const response = await dashboardRolesApi.getDashboardProfile()
      setErrorData(null)
      dispatch(
        setDashboardProfile({
          dashboardUser: response.data
        })
      )
    } catch (error) {
      setErrorData(error)
      if (axios.isAxiosError(error)) {
        if (error.response) {
          log.error('Error fetching profile:', error.response.data)
        } else {
          log.error('No response from the server:', error.message)
        }
      } else {
        log.error('Unexpected error:', error)
      }
    }
    isLoading(false)
  }, [dispatch])

  useEffect(() => {
    fetchDashboardProfile()
  }, [fetchDashboardProfile])

  const role = useMemo(() => {
    if (dashboardUser) {
      if (dashboardUser.role.title) {
        return `sidebar_roles.${dashboardUser.role.title}`
      }
    }
    return `sidebar_roles.unknown`
  }, [dashboardUser])

  return (
    <Flex
      w='272px'
      bg='NeutralLight'
      h='100vh'
      p='4'
      direction='column'
      boxShadow={'0px 2.02206px 3.03309px -2.02206px rgba(24, 39, 75, 0.12), 0px 4.04412px 4.04412px -2.02206px rgba(24, 39, 75, 0.08)'}
      position={'sticky'}
      top={'0px'}
      overflow={'auto'}>
      <Flex alignItems='center' mb='8' padding={'20px'}>
        <Logo />
      </Flex>
      <VStack
        align='start'
        padding={'0px'}
        borderRadius={'8px'}
        gap={'0px'}
        boxShadow={'0px -2px 13.1px -1.01103px rgba(24, 39, 75, 0.05), 0px 2.02206px 7.5px -1.01103px rgba(24, 39, 75, 0.08)'}>
        <MenuItem
          icon='bar-chart-outline'
          label={t('stat')}
          borderTopRadius='10px'
          borderBottomRadius='0px'
          to='/graph-dashboard'
          isSelected={location.pathname.includes('/graph-dashboard')}
        />
        <MenuItem
          icon='people-outline'
          label={t('users.title')}
          borderTopRadius='0px'
          borderBottomRadius='0px'
          to='/users'
          isSelected={location.pathname.includes('/users')}
        />
        <MenuItem
          icon='calendar-outline'
          label={t('events')}
          borderTopRadius='0px'
          borderBottomRadius={canSeeReviews || canSeeActivities ? '0px' : '10px'}
          to='/events'
          isSelected={location.pathname.includes('/events')}
        />
        {canSeeReviews && (
          <MenuItem
            icon='star-outline'
            label={t('reviews_moderation')}
            borderTopRadius='0px'
            borderBottomRadius={canSeeActivities ? '0px' : canSeeRoles ? '0px' : '10px'}
            to='/reviews'
            isSelected={location.pathname.includes('/reviews')}
          />
        )}
        {canSeeActivities && (
          <MenuItem
            icon='checkmark-circle-outline'
            label={t('categories_and_activities')}
            borderTopRadius='0px'
            borderBottomRadius={canSeeRoles ? '0px' : '10px'}
            to='/categories-and-activities'
            isSelected={location.pathname.includes('/categories-and-activities')}
          />
        )}
        {canSeeRoles && (
          <MenuItem
            icon='unlock-outline'
            label={t('roles')}
            borderTopRadius='0px'
            borderBottomRadius='10px'
            to='/roles'
            isSelected={location.pathname.includes('/roles')}
          />
        )}
      </VStack>
      {errorData ? (
        <Flex mt='auto' direction='column' alignItems='start' justifyContent='flex-end'>
          <Divider opacity={'1'} />
          <Flex direction='column' justifyContent='center' alignItems={'flex-start'} padding={'18px 20px'} gap={'5px'}>
            <Button
              variant='outline'
              height='50px'
              color={'primary.400'}
              borderColor={'primary.400'}
              onClick={fetchDashboardProfile}
              margin={'5px'}
              isDisabled={loading}>
              {t('sidebar.retry')}
            </Button>
          </Flex>
          <Divider opacity={'1'} />
          <HStack mt='4' spacing='2' cursor='pointer' onClick={handleLogOut} padding={'18px 20px'}>
            <i data-eva={'log-out-outline'}></i>
            <Text>{t('exit')}</Text>
          </HStack>
        </Flex>
      ) : (
        <Flex mt='auto' direction='column' alignItems='start' justifyContent='flex-end'>
          <Divider opacity={'1'} />
          <Flex direction='column' justifyContent='center' alignItems={'flex-start'} padding={'18px 20px'} gap={'5px'}>
            <Text fontWeight='bold'>{`${dashboardUser?.firstName} ${dashboardUser?.lastName}`}</Text>
            <Text fontSize='sm'>{dashboardUser?.email}</Text>
            <Text
              fontSize='sm'
              color='gray.500'
              border={'1px solid rgba(19, 19, 19, 0.6)'}
              borderRadius='600px'
              alignItems={'center'}
              padding={'4px 6px'}
              gap={'8px'}>
              {t(role)}
            </Text>
          </Flex>
          <Divider opacity={'1'} />
          <HStack mt='4' spacing='2' cursor='pointer' onClick={handleLogOut} padding={'18px 20px'}>
            <i data-eva={'log-out-outline'}></i>
            <Text>{t('exit')}</Text>
          </HStack>
        </Flex>
      )}
    </Flex>
  )
}

export { Sidebar }
